// procurement export — writes the cumulative order data to an .xlsx that mirrors the yard's SHEET 27:
// a SHEET tab (one row per vessel + a TOTALS row counting each option), an ADDITIONAL REQUESTS tab, a
// SEAKEEPERS tab listing the boats that have one written in (when any do), and for the Warehouse a
// WATCH-IT tab the same way.

/* eslint-disable @typescript-eslint/no-explicit-any */

import { ORDER_SHEET_COLUMNS } from '@technohull/shared';
import { OPTION_COLUMNS, AR_COLUMNS, countSeakeepers, countWatchIts, isBlankCell, type ProcVessel, type SheetData } from './procurement.js';

export async function exportProcurementXlsx(vessels: ProcVessel[], sheet: SheetData, title: string, opts: { watchIt?: boolean } = {}): Promise<string> {
  const XLSX: any = await import('xlsx');

  // where each vessel's stored cells live (its key, falling back to SAP for overview vessels).
  const keyOf = (v: ProcVessel) => v.key ?? v.sap;

  // ── SHEET: identity + every spec column, a row per vessel, then a totals row.
  const head = ['SAP', 'MODEL', 'NAME', ...ORDER_SHEET_COLUMNS.map((c) => c.label)];
  const rows = vessels.map((v) => [v.sap, v.model, v.name, ...ORDER_SHEET_COLUMNS.map((c) => sheet[keyOf(v)]?.[c.key] ?? '')]);
  const optionKeys = new Set(OPTION_COLUMNS.map((c) => c.key));
  const totals: (string | number)[] = ['TOTALS', '', ''];
  for (const c of ORDER_SHEET_COLUMNS) {
    if (!optionKeys.has(c.key)) { totals.push(''); continue; } // AR columns aren't counted
    let n = 0;
    for (const v of vessels) if (!isBlankCell(sheet[keyOf(v)]?.[c.key])) n += 1;
    totals.push(n);
  }
  const wsSheet = XLSX.utils.aoa_to_sheet([head, ...rows, totals]);

  // ── ADDITIONAL REQUESTS: a row per vessel that has any, AR1…AR10.
  const arHead = ['SAP', 'MODEL', 'NAME', ...AR_COLUMNS.map((c) => c.label)];
  const arRows = vessels
    .map((v) => ({ v, items: AR_COLUMNS.map((c) => (isBlankCell(sheet[keyOf(v)]?.[c.key]) ? '' : sheet[keyOf(v)]![c.key]!.trim())) }))
    .filter((x) => x.items.some((i) => i !== ''))
    .map((x) => [x.v.sap, x.v.model, x.v.name, ...x.items]);
  const wsAr = XLSX.utils.aoa_to_sheet([arHead, ...arRows]);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsSheet, 'SHEET');
  XLSX.utils.book_append_sheet(wb, wsAr, 'ADDITIONAL REQUESTS');

  // ── SEAKEEPERS: each boat with one written in anywhere on its sheet, and a total to order.
  const sk = countSeakeepers(vessels, sheet);
  if (sk.vessels.length) {
    const skHead = ['SAP', 'NAME', 'MODEL', 'QTY', 'SEAKEEPER MODEL', 'WRITTEN ON THE SHEET'];
    const skRows: (string | number)[][] = sk.vessels.map((v) => [
      v.sap, v.name, v.model, v.qty, v.models.join(' / '), v.mentions.map((m) => `${m.label}: ${m.text}`).join(' | '),
    ]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([skHead, ...skRows, ['TOTAL', '', '', sk.units, '', '']]), 'SEAKEEPERS');
  }
  // ── WATCH-IT (Warehouse): each boat with a Watch-it anti-collision written in (one each), and a total.
  const wi = opts.watchIt ? countWatchIts(vessels, sheet) : null;
  if (wi?.vessels.length) {
    const wiHead = ['SAP', 'NAME', 'MODEL', 'WRITTEN ON THE SHEET'];
    const wiRows: (string | number)[][] = wi.vessels.map((v) => [v.sap, v.name, v.model, v.mentions.map((m) => `${m.label}: ${m.text}`).join(' | ')]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([wiHead, ...wiRows, ['TOTAL', wi.vessels.length, '', '']]), 'WATCH-IT');
  }
  const filename = `${title}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}
